import { z } from "zod"

import {
  platformAccessReviewIdSchema,
  platformApprovalIdSchema,
  platformCommandIdSchema,
  platformOperatorIdSchema,
  platformRoleAssignmentIdSchema,
} from "./identifiers.js"
import {
  platformOperatorV1Schema,
  platformRoleAssignmentV1Schema,
} from "./platform-workforce-v1.js"

const timestampSchema = z.string().datetime({ offset: true })
const reasonSchema = z.string().trim().min(8).max(512)

export const platformWorkforceActionV1Schema = z.enum([
  "operator_invited",
  "role_change_requested",
  "role_change_approved",
  "operator_activated",
  "operator_suspended",
  "operator_reactivated",
  "operator_deactivated",
  "access_review_completed",
])

export const platformWorkforceEvidenceV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformCommandId: platformCommandIdSchema,
    action: platformWorkforceActionV1Schema,
    actorPlatformOperatorId: platformOperatorIdSchema,
    targetPlatformOperatorId: platformOperatorIdSchema,
    reason: reasonSchema,
    occurredAt: timestampSchema,
    resultingVersion: z.number().int().positive().safe(),
  })
  .strict()

export const platformWorkforceApprovalV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformApprovalId: platformApprovalIdSchema,
    targetPlatformOperatorId: platformOperatorIdSchema,
    action: z.enum(["joiner", "assign_role", "revoke_role", "reactivate"]),
    assignmentId: platformRoleAssignmentIdSchema.nullable(),
    requestedByPlatformOperatorId: platformOperatorIdSchema,
    approvedByPlatformOperatorId: platformOperatorIdSchema.nullable(),
    status: z.enum(["pending", "approved", "cancelled", "expired"]),
    reason: reasonSchema,
    requestedAt: timestampSchema,
    expiresAt: timestampSchema,
    decidedAt: timestampSchema.nullable(),
  })
  .strict()
  .superRefine((approval, context) => {
    if (Date.parse(approval.expiresAt) <= Date.parse(approval.requestedAt)) {
      context.addIssue({
        code: "custom",
        path: ["expiresAt"],
        message: "Platform workforce approval expiry must follow request time.",
      })
    }
    const decided = approval.approvedByPlatformOperatorId !== null
      && approval.decidedAt !== null
    if (approval.status === "approved" && !decided) {
      context.addIssue({
        code: "custom",
        message: "Approved workforce change requires approver and decision time.",
      })
    }
    if (approval.status !== "approved"
      && approval.approvedByPlatformOperatorId !== null) {
      context.addIssue({
        code: "custom",
        message: "Only an approved workforce change names an approver.",
      })
    }
    if (approval.requestedByPlatformOperatorId
      === approval.approvedByPlatformOperatorId) {
      context.addIssue({
        code: "custom",
        path: ["approvedByPlatformOperatorId"],
        message: "Workforce change requester cannot approve the same change.",
      })
    }
  })

export const platformAccessReviewV1Schema = z
  .object({
    contractVersion: z.literal(1),
    platformAccessReviewId: platformAccessReviewIdSchema,
    targetPlatformOperatorId: platformOperatorIdSchema,
    reviewerPlatformOperatorId: platformOperatorIdSchema,
    decision: z.enum(["reaffirm", "revoke_assignments", "suspend"]),
    reviewedAssignmentIds: z.array(platformRoleAssignmentIdSchema).max(64),
    reason: reasonSchema,
    reviewedAt: timestampSchema,
    nextReviewDueAt: timestampSchema,
  })
  .strict()
  .superRefine((review, context) => {
    if (review.targetPlatformOperatorId === review.reviewerPlatformOperatorId) {
      context.addIssue({
        code: "custom",
        path: ["reviewerPlatformOperatorId"],
        message: "Platform Operator cannot review their own access.",
      })
    }
    if (new Set(review.reviewedAssignmentIds).size
      !== review.reviewedAssignmentIds.length) {
      context.addIssue({
        code: "custom",
        path: ["reviewedAssignmentIds"],
        message: "Reviewed assignments must be unique.",
      })
    }
    if (Date.parse(review.nextReviewDueAt) <= Date.parse(review.reviewedAt)) {
      context.addIssue({
        code: "custom",
        path: ["nextReviewDueAt"],
        message: "Next access review must follow the completed review.",
      })
    }
  })

export const platformWorkforceSnapshotV1Schema = z
  .object({
    contractVersion: z.literal(1),
    operator: platformOperatorV1Schema,
    assignments: z.array(platformRoleAssignmentV1Schema).max(64),
    pendingApprovals: z.array(platformWorkforceApprovalV1Schema).max(64),
    latestAccessReview: platformAccessReviewV1Schema.nullable(),
    nextReviewDueAt: timestampSchema,
  })
  .strict()

export const platformWorkforceMutationResultV1Schema = z
  .object({
    contractVersion: z.literal(1),
    snapshot: platformWorkforceSnapshotV1Schema,
    evidence: platformWorkforceEvidenceV1Schema,
  })
  .strict()

export type PlatformAccessReviewV1 = z.infer<typeof platformAccessReviewV1Schema>
export type PlatformWorkforceApprovalV1 = z.infer<
  typeof platformWorkforceApprovalV1Schema
>
export type PlatformWorkforceEvidenceV1 = z.infer<
  typeof platformWorkforceEvidenceV1Schema
>
export type PlatformWorkforceMutationResultV1 = z.infer<
  typeof platformWorkforceMutationResultV1Schema
>
export type PlatformWorkforceSnapshotV1 = z.infer<
  typeof platformWorkforceSnapshotV1Schema
>
